import PageFilters, { Filter } from 'components/PageFilters';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import TextField from 'components/FormElements/AppTextInput';
import Autocomplete from 'components/FormElements/AppAutocomplete';
import { DISPLAY_FILTER_OPTIONS, DisplayFilterOption } from '../constants';
import { FirmSelector } from 'components/FirmSelector';
import { useGetRole } from 'hooks/ui/useGetRole';
import { Box, styled } from '@mui/material';

interface InvestmentFilters {
  displayFilters: Array<DisplayFilterOption>;
  search: string;
  fundId: string | null;
  firmId: string | null;
}

interface InvestmentFiltersProps {
  filters: InvestmentFilters;
  handleChangeFilters: (filters: Partial<InvestmentFilters>) => void;
  exportExcelSheet: () => void;
  tranchesList: Array<{
    _id: string;
    firmId: { firmName: string };
    fundName: string;
  }>;
  isExporting: boolean;
}

const Placeholder = styled('span')(() => ({
  color: '#999999',
}));

export const InvestmentFilters: React.FC<InvestmentFiltersProps> = ({
  filters,
  handleChangeFilters,
  exportExcelSheet,
  tranchesList,
  isExporting,
}) => {
  const { isSuperAdmin } = useGetRole();

  const handleDisplayFiltersChange = (event) => {
    const rawValue = event.target.value;
    const value = Array.isArray(rawValue)
      ? rawValue
      : typeof rawValue === 'string'
      ? [rawValue]
      : [];

    if (value.includes('all')) {
      if (isCheckedAll) {
        handleChangeFilters({
          displayFilters: [],
        });
      } else {
        handleChangeFilters({
          displayFilters: DISPLAY_FILTER_OPTIONS.map((opt) => opt.value),
        });
      }
    } else {
      handleChangeFilters({
        displayFilters: value.filter(
          (v): v is DisplayFilterOption => v !== 'all',
        ),
      });
    }
  };

  const isCheckedAll =
    filters.displayFilters.length === DISPLAY_FILTER_OPTIONS.length;

  return (
    <PageFilters
      pageId="incomplete-investments-table"
      buttons={[
        {
          disabled: isExporting,
          label: 'Export',
          onClick: exportExcelSheet,
        },
      ]}
    >
      <Filter>
        <TextField
          type="search"
          name="searchInvestorsByName"
          value={filters.search}
          placeholder="Search by investor or adviser"
          onChange={(event) => {
            handleChangeFilters({
              search: event.target.value,
            });
          }}
        />
      </Filter>
      {isSuperAdmin && (
        <Filter>
          <FirmSelector
            firmId={filters.firmId || undefined}
            onChange={(newFirmId) => {
              handleChangeFilters({
                firmId: newFirmId || null,
              });
            }}
            placeholder="Select firm"
          />
        </Filter>
      )}
      <Filter>
        <Autocomplete
          id="fundId"
          value={
            filters.fundId && tranchesList
              ? tranchesList.find(({ _id }) => _id === filters.fundId) ?? null
              : null
          }
          options={tranchesList ?? []}
          getOptionLabel={(option) =>
            option ? `${option?.firmId?.firmName} ${option?.fundName}` : ''
          }
          getOptionKey={(option) => option._id}
          onChange={(_, newValue) => {
            handleChangeFilters({
              ...filters,
              fundId: newValue?._id ?? null,
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Select tranche"
            />
          )}
        />
      </Filter>
      <Filter>
        <AppSelectBox
          data={DISPLAY_FILTER_OPTIONS}
          value={filters.displayFilters}
          valueKey="value"
          labelKey="label"
          placeholder="Select filters"
          multiple
          isCheckedAll={isCheckedAll}
          onChange={handleDisplayFiltersChange}
          selectAllLabel="Show all"
          // @ts-expect-error
          renderValue={(selected: Array<DisplayFilterOption>) => (
            <Box>
              {selected.length === 0 ? (
                <Placeholder>Select filters</Placeholder>
              ) : selected.length === DISPLAY_FILTER_OPTIONS.length ? (
                'Show all'
              ) : (
                selected
                  .map((filterValue) => {
                    return DISPLAY_FILTER_OPTIONS.find(
                      (opt) => opt.value === filterValue,
                    )?.label;
                  })
                  .filter(Boolean)
                  .join(', ')
              )}
            </Box>
          )}
        />
      </Filter>
    </PageFilters>
  );
};
